import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Fetch all clinics
export const fetchClinics = createAsyncThunk(
  "clinics/fetchAll",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/clinics`, config);
      return response.data; // Return the clinics array directly
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Fetch a single clinic by ID
export const fetchClinicById = createAsyncThunk(
  "clinics/fetchById",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`Fetching clinic with ID: ${clinicId}`);
      const response = await axios.get(
        `${baseURL}/api/clinics/${clinicId}`,
        config
      );
      console.log("Clinic fetched successfully:", response.data);

      return response.data;
    } catch (error) {
      console.error("Error fetching clinic:", error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Create a new clinic
export const createClinic = createAsyncThunk(
  "clinics/create",
  async (clinicData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/clinics`,
        clinicData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update a clinic
export const updateClinic = createAsyncThunk(
  "clinics/update",
  async ({ id, clinicData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/clinics/${id}`,
        clinicData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Delete a clinic
export const deleteClinic = createAsyncThunk(
  "clinics/delete",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      await axios.delete(`${baseURL}/api/clinics/${clinicId}`, config);
      return clinicId; // Return the deleted clinic's ID
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const addClinicianToClinic = createAsyncThunk(
  "clinics/addClinician",
  async (
    { clinicId, clinicianId },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/clinics/${clinicId}/addClinician/${clinicianId}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const removeClinicianFromClinic = createAsyncThunk(
  "clinics/removeClinician",
  async (
    { clinicId, clinicianId },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(
        `${baseURL}/api/clinics/${clinicId}/removeClinician/${clinicianId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const addRoleToClinic = createAsyncThunk(
  "clinics/addRole",
  async ({ clinicId, roleId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/clinics/${clinicId}/addRole/${roleId}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const removeRoleFromClinic = createAsyncThunk(
  "clinics/removeRole",
  async ({ clinicId, roleId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(
        `${baseURL}/api/clinics/${clinicId}/removeRole/${roleId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const addStaffToClinic = createAsyncThunk(
  "clinics/addStaff",
  async ({ clinicId, staffId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/clinics/${clinicId}/addStaff/${staffId}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const removeStaffFromClinic = createAsyncThunk(
  "clinics/removeStaff",
  async ({ clinicId, staffId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(
        `${baseURL}/api/clinics/${clinicId}/removeStaff/${staffId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const addServiceToClinic = createAsyncThunk(
  "clinics/addService",
  async ({ clinicId, serviceId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/clinics/${clinicId}/addService/${serviceId}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const removeServiceFromClinic = createAsyncThunk(
  "clinics/removeService",
  async ({ clinicId, serviceId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(
        `${baseURL}/api/clinics/${clinicId}/removeService/${serviceId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicStaff = createAsyncThunk(
  "clinics/fetchStaff",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinics/${clinicId}/staff`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk for fetching available staff to add to a clinic
export const fetchAvailableStaff = createAsyncThunk(
  "clinics/fetchAvailableStaff",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/clinics/${clinicId}/available-staff`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicServices = createAsyncThunk(
  "clinics/fetchServices",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinics/${clinicId}/services`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicRoles = createAsyncThunk(
  "clinics/fetchRoles",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinics/${clinicId}/roles`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchAllServices = createAsyncThunk(
  "clinics/fetchAllServices",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/services`, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const deleteClinicService = createAsyncThunk(
  "clinics/deleteService",
  async ({ clinicId, serviceId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(
        `${baseURL}/api/clinics/${clinicId}/removeService/${serviceId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicPaymentMethods = createAsyncThunk(
  "clinics/fetchPaymentMethods",
  async (clinicId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinics/payment-methods/${clinicId}`,
        config
      );
      return response.data.paymentMethods;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Add payment method to clinic using update route
export const addPaymentMethodToClinic = createAsyncThunk(
  "clinics/addPaymentMethod",
  async ({ clinicId, paymentMethodData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Clinic Thunk] Adding payment method to clinic ${clinicId}:`, paymentMethodData);
      
      // First get the current clinic to access existing payment methods
      const clinicResponse = await axios.get(`${baseURL}/api/clinics/${clinicId}`, config);
      const currentClinic = clinicResponse.data;
      
      // Validate that the description doesn't already exist
      const existingMethod = currentClinic.paymentMethods.find(
        method => method.englishDescription.toLowerCase().trim() === paymentMethodData.englishDescription.toLowerCase().trim()
      );
      
      if (existingMethod) {
        throw new Error(`Payment method "${paymentMethodData.englishDescription}" already exists for this clinic`);
      }

      // Auto-generate QBO code (sequential number starting from 1)
      const maxQboCode = currentClinic.paymentMethods.reduce((max, method) => {
        const code = parseInt(method.qboCode);
        return isNaN(code) ? max : Math.max(max, code);
      }, 0);
      const qboCode = (maxQboCode + 1).toString();

      // Auto-generate value from description
      const value = paymentMethodData.englishDescription
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      // Ensure value is unique
      let finalValue = value;
      let suffix = 1;
      while (currentClinic.paymentMethods.find(method => method.value === finalValue)) {
        finalValue = `${value}-${suffix}`;
        suffix++;
      }

      const newPaymentMethod = { 
        qboCode, 
        englishDescription: paymentMethodData.englishDescription.trim(), 
        value: finalValue 
      };

      // Add new payment method to existing array
      const updatedPaymentMethods = [...currentClinic.paymentMethods, newPaymentMethod];
      
      // Update clinic with new payment methods array
      const response = await axios.put(
        `${baseURL}/api/clinics/${clinicId}`,
        { paymentMethods: updatedPaymentMethods },
        config
      );
      
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Remove payment method from clinic using update route
export const removePaymentMethodFromClinic = createAsyncThunk(
  "clinics/removePaymentMethod",
  async ({ clinicId, paymentMethodId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Clinic Thunk] Removing payment method ${paymentMethodId} from clinic ${clinicId}`);
      
      // First get the current clinic to access existing payment methods
      const clinicResponse = await axios.get(`${baseURL}/api/clinics/${clinicId}`, config);
      const currentClinic = clinicResponse.data;
      
      // Remove the payment method by filtering out the one with matching ID
      const updatedPaymentMethods = currentClinic.paymentMethods.filter(
        method => method._id.toString() !== paymentMethodId
      );
      
      if (updatedPaymentMethods.length === currentClinic.paymentMethods.length) {
        throw new Error('Payment method not found');
      }
      
      // Update clinic with filtered payment methods array
      const response = await axios.put(
        `${baseURL}/api/clinics/${clinicId}`,
        { paymentMethods: updatedPaymentMethods },
        config
      );
      
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Copy payment methods between clinics using update route
export const copyPaymentMethodsBetweenClinics = createAsyncThunk(
  "clinics/copyPaymentMethods",
  async ({ targetClinicId, sourceClinicId, replaceExisting = false }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Clinic Thunk] Copying payment methods from ${sourceClinicId} to ${targetClinicId}, replace: ${replaceExisting}`);
      
      // Get both clinics
      const [sourceResponse, targetResponse] = await Promise.all([
        axios.get(`${baseURL}/api/clinics/${sourceClinicId}`, config),
        axios.get(`${baseURL}/api/clinics/${targetClinicId}`, config)
      ]);
      
      const sourceClinic = sourceResponse.data;
      const targetClinic = targetResponse.data;
      
      if (!sourceClinic.paymentMethods || sourceClinic.paymentMethods.length === 0) {
        throw new Error('Source clinic has no payment methods to copy');
      }

      let updatedPaymentMethods;
      let copiedCount = 0;
      let skippedCount = 0;

      if (replaceExisting) {
        // Replace all payment methods
        updatedPaymentMethods = [...sourceClinic.paymentMethods];
        copiedCount = sourceClinic.paymentMethods.length;
      } else {
        // Start with existing methods
        updatedPaymentMethods = [...targetClinic.paymentMethods];
        
        // Add only new payment methods (avoid duplicates)
        for (const sourceMethod of sourceClinic.paymentMethods) {
          const existingMethod = updatedPaymentMethods.find(
            method => method.qboCode === sourceMethod.qboCode || method.value === sourceMethod.value
          );

          if (!existingMethod) {
            // Remove _id to let MongoDB generate a new one
            const { _id, ...methodData } = sourceMethod;
            updatedPaymentMethods.push(methodData);
            copiedCount++;
          } else {
            skippedCount++;
          }
        }
      }
      
      // Update target clinic with new payment methods
      const response = await axios.put(
        `${baseURL}/api/clinics/${targetClinicId}`,
        { paymentMethods: updatedPaymentMethods },
        config
      );
      
      return {
        clinic: response.data,
        sourceClinic: sourceClinic.name,
        targetClinic: targetClinic.name,
        copiedCount,
        skippedCount,
        totalPaymentMethods: updatedPaymentMethods.length,
        replaceExisting
      };
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
